// @ts-nocheck

import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { useToast } from "@/components/ui/ToastProvider";
import { formatCount, formatTimeAgo, formatShortDate } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { IconSearch, IconShield, IconCheck } from "@/components/ui/Icons";

interface AdminUser {
  id: string; username: string; display_name: string;
  avatar_url: string | null; email: string; role: string;
  is_verified: boolean; is_banned: boolean;
  karma_score: number; post_count: number; comment_count: number;
  joined_at: string; last_active_at: string;
}

interface AdminUsersClientProps {
  users: AdminUser[];
}

export function AdminUsersClient({ users: initial }: AdminUsersClientProps) {
  const [users, setUsers] = useState<AdminUser[]>(initial);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const { success, error: showError } = useToast();

  const filtered = users.filter(u => {
    const matchesSearch = !searchQuery ||
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === "all" || u.role === filterRole;
    const matchesStatus = filterStatus === "all" ||
      (filterStatus === "banned" && u.is_banned) ||
      (filterStatus === "verified" && u.is_verified) ||
      (filterStatus === "active" && !u.is_banned);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleAction = async (userId: string, action: "ban" | "unban" | "verify" | "unverify" | "make_mod" | "remove_mod") => {
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error();

      setUsers(prev => prev.map(u => {
        if (u.id !== userId) return u;
        if (action === "ban") return { ...u, is_banned: true };
        if (action === "unban") return { ...u, is_banned: false };
        if (action === "verify") return { ...u, is_verified: true };
        if (action === "unverify") return { ...u, is_verified: false };
        if (action === "make_mod") return { ...u, role: "moderator" };
        if (action === "remove_mod") return { ...u, role: "user" };
        return u;
      }));
      success("Updated", `User ${action} successful.`);
    } catch {
      showError("Action failed");
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-subtle pointer-events-none" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search users..." className="input pl-9 w-full" />
        </div>
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="input w-auto">
          <option value="all">All roles</option>
          <option value="user">Users</option>
          <option value="moderator">Moderators</option>
          <option value="admin">Admins</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input w-auto">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="banned">Banned</option>
          <option value="verified">Verified</option>
        </select>
      </div>

      <p className="text-xs text-text-muted">{filtered.length} users</p>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-surface-overlay">
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Karma</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Posts</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Joined</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user, i) => (
                <motion.tr key={user.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar src={user.avatar_url} alt={user.display_name} size="sm" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1">
                          <Link to={`/profile/${user.username}`}
                            className="text-sm font-medium text-foreground hover:text-brand transition-colors truncate">
                            {user.display_name}
                          </Link>
                          {user.is_verified && <VerifiedBadge size="xs" />}
                        </div>
                        <p className="text-xs text-text-subtle">@{user.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={user.role === "admin" ? "destructive" : user.role === "moderator" ? "warning" : "muted"} size="sm" className="capitalize">
                      {user.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-foreground tabular-nums">
                    {formatCount(user.karma_score)}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-muted tabular-nums">
                    {formatCount(user.post_count)}
                  </td>
                  <td className="px-4 py-3 text-xs text-text-subtle">
                    {formatShortDate(user.joined_at)}
                  </td>
                  <td className="px-4 py-3">
                    {user.is_banned
                      ? <Badge variant="destructive" size="sm">Banned</Badge>
                      : <Badge variant="success" size="sm">Active</Badge>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {user.is_banned ? (
                        <button onClick={() => handleAction(user.id, "unban")}
                          className="btn-sm btn-secondary text-xs">Unban</button>
                      ) : (
                        <button onClick={() => handleAction(user.id, "ban")}
                          className="btn-sm btn-destructive text-xs">Ban</button>
                      )}
                      {!user.is_verified ? (
                        <button onClick={() => handleAction(user.id, "verify")}
                          className="btn-sm btn-secondary text-xs">Verify</button>
                      ) : (
                        <button onClick={() => handleAction(user.id, "unverify")}
                          className="btn-sm btn-ghost text-xs text-text-muted">Unverify</button>
                      )}
                      {user.role === "user" && (
                        <button onClick={() => handleAction(user.id, "make_mod")}
                          className="btn-sm btn-ghost text-xs">Make mod</button>
                      )}
                      {user.role === "moderator" && (
                        <button onClick={() => handleAction(user.id, "remove_mod")}
                          className="btn-sm btn-ghost text-xs text-text-muted">Remove mod</button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
